import { segment } from '@actions/analytics';
import { TEST_SERIES_TAB } from '@cont/SeeAll/TestSeriesListing/Common/TestSeriesTabSwitcher';
import {
  ALL_TEST_SERIES,
  CHECKOUT_STARTED,
  TEST_PAGE,
  TEST_RANDOM_TEST_CTA_CLICKED,
  TEST_RANDOM_TEST_SELECTED,
  TEST_SUBSCRIPTION_SEGMENT_CLICKED,
  TEST_TEST_CARD_CLICKED,
  TEST_TEST_SERIES_VIEWED,
  TEST_TESTS_SECTION_CLICKED,
  TEST_TESTS_SUB_SECTION_CLICKED
} from '@constants/segment';
import { getIsCbtProgramme } from '@cont/Quiz/Details/utils';
import { getPrimeGlobalProps } from '@stores/prime-quota/selectors';
import segmentStorage from '@events/segmentStorage';
import { localStorageAvailable } from '@utils/localStorage';
import { UI_SUBSCRIPTION } from './constants';

const TEST_TAB_ANALYTICS_CONTEXT_KEY = 'test_tab_analytics_context';
const TEST_TAB_SECTION = 'Test Tab';
const RANDOM_TEST_EXHAUSTED_SECTION = 'Random Test Exhausted';
const RANDOM_TEST_SECTION_BY_BUCKET = {
  short: 'Random Test Section 1',
  medium: 'Random Test Section 2',
  long: 'Random Test Section 3'
};

const sanitized = (value, fallback = 'NA') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const TEST_SECTION_LABELS = {
  [TEST_SERIES_TAB.ALL]: 'All Tests',
  [TEST_SERIES_TAB.MY]: 'My Tests'
};

const ALL_TESTS_SUBSECTION_LABELS = {
  upcoming: 'Upcoming Tests',
  ongoing: 'Ongoing Tests',
  previous_year: 'Previous Year Tests',
  past: 'Past Tests',
  educator: 'Educator Tests',
  all_series: 'All Test Series'
};

const MY_TESTS_SUBSECTION_LABELS = {
  your_upcoming: 'Your upcoming tests',
  your_attempt: 'Your Attempt'
};

const getTestSectionName = (view) => TEST_SECTION_LABELS[view] || 'All Tests';

const getTestSubsectionMeta = (view, categoryTabId) => {
  const labels =
    view === TEST_SERIES_TAB.MY
      ? MY_TESTS_SUBSECTION_LABELS
      : ALL_TESTS_SUBSECTION_LABELS;
  const entries = Object.entries(labels);
  const index = entries.findIndex(([id]) => id === categoryTabId);

  return {
    name: labels[categoryTabId] || categoryTabId,
    rank: index >= 0 ? index + 1 : 1
  };
};

const getSubscriptionFilterValue = (subscriptionIds = []) => {
  if (
    subscriptionIds.includes(UI_SUBSCRIPTION.PLUS) ||
    subscriptionIds.includes(UI_SUBSCRIPTION.ICONIC)
  ) {
    return 'Plus & Iconic';
  }

  if (subscriptionIds.includes(UI_SUBSCRIPTION.PRIME)) {
    return 'Prime';
  }

  return 'All';
};

const getSelectedOptionLabels = (options = [], selectedIds = [], filterId) =>
  selectedIds
    .map((id) => {
      const option = options.find((item) => item.id === id);
      if (!option) return id;
      if (filterId === 'educator') {
        return option.name || option.label;
      }
      return option.label;
    })
    .filter(Boolean);

const mapAppliedFiltersToEventProps = (appliedSelections) => {
  const status = appliedSelections?.status?.[0];
  const testType = appliedSelections?.test_type?.[0];
  const educators = appliedSelections?.educator || [];

  return {
    test_status_selected: status || 'All',
    test_type_selected: testType || 'All',
    no_of_educators_filtered:
      educators.length > 0 ? String(educators.length) : 'NA'
  };
};

const mapCardClickFilterProps = (appliedSelections = {}, filterConfig = []) => {
  const educators = appliedSelections.educator || [];
  const enrollment = appliedSelections.enrollment?.[0];
  const educatorFilter = filterConfig.find((item) => item.id === 'educator');
  const educatorNames = getSelectedOptionLabels(
    educatorFilter?.options,
    educators,
    'educator'
  );

  return {
    ...mapAppliedFiltersToEventProps(appliedSelections),
    educator_names_filtered:
      educatorNames.length > 0 ? educatorNames.join(', ') : 'NA',
    test_enrollment_selected: enrollment || 'All',
    filter_value: getSubscriptionFilterValue(appliedSelections.subscription)
  };
};

const getTestTabExperience = (isTestTabV2) =>
  isTestTabV2 ? 'revamped' : 'legacy';

export const getRandomTestSectionLabel = (durationBucket) =>
  RANDOM_TEST_SECTION_BY_BUCKET[durationBucket] ||
  RANDOM_TEST_EXHAUSTED_SECTION;

export const buildTestTabBaseProps = ({
  view,
  categoryTabId,
  appliedSelections,
  isTestTabV2 = false
}) => {
  const subsection = getTestSubsectionMeta(view, categoryTabId);

  return {
    test_section_name: getTestSectionName(view),
    current_tab: 'Tests',
    test_subsection_name: subsection.name,
    test_subsection_rank: subsection.rank,
    test_tab_experience: getTestTabExperience(isTestTabV2),
    ...mapAppliedFiltersToEventProps(appliedSelections)
  };
};

export const saveTestTabAnalyticsContext = (context) => {
  if (!localStorageAvailable()) return;
  localStorage.setItem(TEST_TAB_ANALYTICS_CONTEXT_KEY, JSON.stringify(context));
};

const getTestTabAnalyticsContext = () => {
  if (!localStorageAvailable()) return {};
  try {
    return JSON.parse(
      localStorage.getItem(TEST_TAB_ANALYTICS_CONTEXT_KEY) || '{}'
    );
  } catch {
    return {};
  }
};

const getTestTabEventProps = (overrides = {}) => ({
  ...getTestTabAnalyticsContext(),
  ...overrides,
  ...getPrimeGlobalProps()
});

export const trackTestTestsSectionClicked = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TESTS_SECTION_CLICKED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const trackTestTestsSubSectionClicked = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TESTS_SUB_SECTION_CLICKED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    current_tab: sanitized(eventProps.current_tab, 'Tests'),
    test_subsection_name: sanitized(eventProps.test_subsection_name),
    test_subsection_rank: eventProps.test_subsection_rank,
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const trackTestTestSeriesViewed = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TEST_SERIES_VIEWED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    current_tab: sanitized(eventProps.current_tab, 'Tests'),
    test_subsection_name: sanitized(eventProps.test_subsection_name),
    test_subsection_rank: eventProps.test_subsection_rank,
    test_status_selected: sanitized(eventProps.test_status_selected),
    test_type_selected: sanitized(eventProps.test_type_selected),
    no_of_educators_filtered: sanitized(eventProps.no_of_educators_filtered),
    test_series_title: sanitized(eventProps.test_series_title),
    test_series_uid: sanitized(eventProps.test_series_uid),
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const getTestTabContextForQuizEvents = () => {
  const context = getTestTabAnalyticsContext();
  if (!context.test_section_name) return {};

  return {
    test_section_name: context.test_section_name,
    current_tab: context.current_tab || 'Tests',
    test_subsection_name: context.test_subsection_name,
    test_subsection_rank: context.test_subsection_rank,
    test_status_selected: context.test_status_selected,
    test_type_selected: context.test_type_selected,
    no_of_educators_filtered: context.no_of_educators_filtered,
    test_series_title: context.test_series_title,
    test_series_uid: context.test_series_uid,
    test_uid: context.test_uid,
    test_name: context.test_name,
    is_nta_test: context.is_nta_test ?? false,
    test_tab_experience: context.test_tab_experience || 'legacy',
    ...getPrimeGlobalProps()
  };
};

export const getTestTabSearchEventProps = (lastPrimarySource) => {
  if (lastPrimarySource !== ALL_TEST_SERIES) return {};

  const context = getTestTabAnalyticsContext();
  if (!context.test_section_name) {
    return { current_tab: 'Tests' };
  }

  return {
    test_section_name: context.test_section_name,
    current_tab: context.current_tab || 'Tests',
    test_subsection_name: context.test_subsection_name,
    test_subsection_rank: context.test_subsection_rank,
    test_tab_experience: context.test_tab_experience || 'legacy',
    ...getPrimeGlobalProps()
  };
};

export const getTestTabReportEventProps = (quizData = {}) => {
  const context = getTestTabAnalyticsContext();
  const primeGlobalProps = getPrimeGlobalProps();

  if (!context.test_section_name && !quizData?.uid) {
    return primeGlobalProps;
  }

  return {
    test_section_name: sanitized(context.test_section_name, 'NA'),
    current_tab: sanitized(context.current_tab, 'Tests'),
    test_subsection_name: sanitized(context.test_subsection_name),
    test_subsection_rank: context.test_subsection_rank,
    test_status_selected: sanitized(context.test_status_selected),
    test_type_selected: sanitized(context.test_type_selected),
    no_of_educators_filtered: sanitized(context.no_of_educators_filtered),
    test_series_title: sanitized(
      context.test_series_title || quizData?.collection?.programme?.name
    ),
    test_series_uid: sanitized(
      context.test_series_uid || quizData?.collection?.programme?.uid
    ),
    test_uid: sanitized(context.test_uid || quizData?.uid),
    test_name: sanitized(context.test_name || quizData?.title),
    is_nta_test: getIsCbtProgramme(quizData),
    test_tab_experience: sanitized(context.test_tab_experience, 'legacy'),
    ...primeGlobalProps
  };
};

export const trackTestSubscriptionSegmentClicked = ({
  goalUid,
  goalName,
  filterValue,
  ...baseProps
}) => {
  const eventProps = getTestTabEventProps(baseProps);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_SUBSCRIPTION_SEGMENT_CLICKED, {
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    filter_type: 'subscription_segment',
    filter_value: sanitized(filterValue),
    last_primary_source_section: TEST_TAB_SECTION,
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const trackTestRandomTestCtaClicked = ({
  goalUid,
  goalName,
  action,
  ...baseProps
}) => {
  const eventProps = getTestTabEventProps(baseProps);

  segment.track(TEST_RANDOM_TEST_CTA_CLICKED, {
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    cta_action: sanitized(action),
    last_primary_source: TEST_PAGE,
    last_primary_source_section: TEST_TAB_SECTION,
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const trackTestRandomTestSelected = ({
  goalUid,
  goalName,
  duration,
  durationBucket,
  testUid,
  testName,
  ...baseProps
}) => {
  const eventProps = getTestTabEventProps(baseProps);
  saveTestTabAnalyticsContext({
    ...eventProps,
    test_uid: sanitized(testUid, eventProps.test_uid),
    test_name: sanitized(testName, eventProps.test_name)
  });

  segment.track(TEST_RANDOM_TEST_SELECTED, {
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    duration: sanitized(duration),
    duration_bucket: sanitized(durationBucket),
    last_primary_source: TEST_PAGE,
    last_primary_source_section: TEST_TAB_SECTION,
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...getPrimeGlobalProps()
  });
};

export const trackTestTestCardClicked = ({
  goalUid,
  goalName,
  card = {},
  appliedSelections,
  filterConfig,
  ...baseProps
}) => {
  const eventProps = getTestTabEventProps(baseProps);
  const filterProps = mapCardClickFilterProps(appliedSelections, filterConfig);

  segment.track(TEST_TEST_CARD_CLICKED, {
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    test_uid: sanitized(card.id),
    test_name: sanitized(card.title),
    test_series_title: sanitized(
      card.variant === 'test_series' ? card.title : eventProps.test_series_title
    ),
    test_series_uid: sanitized(
      card.variant === 'test_series' ? card.id : eventProps.test_series_uid
    ),
    duration: card.durationMinutes ? `${card.durationMinutes} min` : 'NA',
    duration_minutes: card.durationMinutes ?? 'NA',
    is_locked: Boolean(card.showLock || card.lockType),
    lock_type: sanitized(card.lockType),
    test_section_name: sanitized(eventProps.test_section_name),
    current_tab: sanitized(eventProps.current_tab, 'Tests'),
    test_subsection_name: sanitized(eventProps.test_subsection_name),
    test_subsection_rank: eventProps.test_subsection_rank,
    test_tab_experience: sanitized(eventProps.test_tab_experience, 'legacy'),
    ...filterProps,
    ...getPrimeGlobalProps()
  });
};

const persistTestPageCheckoutSource = (sourceSection) => {
  segmentStorage.setItem('lps', TEST_PAGE);
  segmentStorage.setItem('lpss', sourceSection);
};

export const trackTestTabCheckoutStarted = ({
  goalUid,
  goalName,
  lastPrimarySourceSection
}) => {
  persistTestPageCheckoutSource(lastPrimarySourceSection);

  segment.track(CHECKOUT_STARTED, {
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    last_primary_source: TEST_PAGE,
    last_primary_source_section: sanitized(lastPrimarySourceSection),
    ...getPrimeGlobalProps()
  });
};
